import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Divider,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { MuiColorInput } from 'mui-color-input';
import { apiRoute } from 'constants/apiRoute';
import { useDispatch, useSelector } from 'react-redux';
import { SET_LOADING, SET_TOAST_OBJECT } from 'store/actions';
import copy from 'copy-to-clipboard';
import CustomizedTooltip from 'Components/CustomizedTooltip';
import { ColorPicker } from 'material-ui-color';
import { checkIsPremiumPlan } from 'helper/checkPlan.helper';
import { axiosInterceptor } from './interceptor';
import PremiumPage from './PremiumPage';
import Image from 'assets/images/dashboard/freemium-register.png';
import { setCurrentPage } from 'store/ExecutiveTeam/actions';
import PermisssionValidationWrapper from 'Components/PermissionWrapper/PermissionValidationWrapper';
import PermissionWrapper from 'Components/PermissionWrapper';
import { PermissionTypes } from 'Components/PermissionWrapper/constants';
import { hasNoPermissionToEdit } from 'helper/permission.helper';

const defaultTheme = {
    primaryColor: '#00214D',
    secondaryColor: '#f9fafb',
    backgroundColor: '#ffffff'
};

const styles = {
    maxWidth: '746px',

    '& .MuiFormControl-root .MuiOutlinedInput-root': {
        background: 'transparent',
        '.MuiInputBase-input': {
            padding: '14.5px 10px 14.5px !important',
            fontWeight: '400',
            color: '#00214D',
            background: 'transparent',
            borderRadius: '8px'
        }
    },

    '& .choose-color': {
        '.ColorPicker-MuiInput-input': {
            display: 'none'
        },
        '.MuiFormControl-root': {
            width: '24px',
            height: '24px',

            '.MuiOutlinedInput-root .MuiInputAdornment-positionStart': {
                margin: '0'
            }
        },

        '& .MuiInputBase-colorPrimary': {
            padding: '0',
            width: '24px',
            height: '24px',

            '.MuiInputBase-input': {
                display: 'none'
            },

            '.MuiOutlinedInput-notchedOutline': {
                display: 'none'
            }
        },

        '.MuiButton-textPrimary': {
            border: '1px solid rgba(125, 125, 130, 0.25)',
            background: '#F9FAFB'
        }
    },

    '& .MuiButton-root': {
        borderRadius: '4px'
    },

    '.cancel': {
        background: '#7D7D82',
        border: '1px solid #7D7D82',
        color: '#fff',

        '&:hover': {
            background: 'transparent',
            borderColor: '#00214d',
            color: '#00214d'
        }
    },

    '.MuiAccordion-root': {
        margin: '0',
        background: 'none',
        '&:before': {
            display: 'none'
        },
        '& .MuiAccordionSummary': {
            '&-root': {
                minHeight: '0',
                padding: '0',
                flexDirection: 'row-reverse',
                gap: '10px'
            },
            '&-content': {
                margin: '0',
                fontSize: '1rem',
                fontWeight: '500',
                color: '#00214D'
            },
            '&-expandIconWrapper': {}
        }
    },

    '.MuiAccordionDetails-root': {
        padding: '24px 0'
    }
};

const WebForm = () => {
    const { companyData } = useSelector((params) => params.customization);
    const { pagePermissions } = useSelector((state) => state.authentication);
    const isAccountPremium = checkIsPremiumPlan(companyData?.subscription?.plan);
    const dispatch = useDispatch();
    const [showCompanyId, setShowCompanyId] = useState(false);
    const [showSecret, setShowSecret] = useState(false);
    const [credentials, setCredentials] = useState({ companyId: '', clientSecret: '', authorizedOrigins: [] });
    const [changedState, setChangedState] = useState({});
    const [link, setLink] = useState('');
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState({});
    const [disableIframe, setDisableIframe] = useState(true);

    const origin = changedState.authorizedOrigins?.value?.[0] ?? '';

    // Needed to add this another state since mui color picker doesnot accept hexstring on color white.
    // It excepts an object

    const [color, setColor] = useState({
        primaryColor: defaultTheme.primaryColor,
        secondaryColor: defaultTheme.secondaryColor,
        backgroundColor: defaultTheme.backgroundColor
    });

    const fetchCredentials = async () => {
        try {
            dispatch({ type: SET_LOADING, loading: true });
            const res = await axiosInterceptor.get(apiRoute.webform.get);
            const { data, ...rest } = res.data;
            const response = { data: { ...rest, ...data } };

            const companyId = response.data.companyId;
            const clientSecret = response.data.clientSecret;

            if (!response.data?.hasOwnProperty('theme')) {
                response.data.theme = defaultTheme;
            }
            setCredentials(response.data);
            const mapData = Object.entries(response.data).reduce((c, [key, value]) => {
                if (key === 'data') {
                    return {
                        ...c,
                        ['authorizedOrigins']: {
                            changed: false,
                            value: value.authorizedOrigins
                        }
                    };
                }

                return {
                    ...c,
                    [key]: {
                        changed: false,
                        value
                    }
                };
            }, {});

            setChangedState(mapData);
            setColor(mapData?.theme?.value ?? defaultTheme);

            setDisableIframe(!mapData.authorizedOrigins?.value?.length);
            fetchLink(companyId, clientSecret);
            fetchPreview(clientSecret);
        } catch (error) {
            dispatch({ type: SET_LOADING, loading: false });
        }
    };

    const fetchLink = async (companyId, client_secret) => {
        try {
            const response = await axiosInterceptor.get(apiRoute.webform.getLink, { headers: { 'client-secret': client_secret } });
            setLink(response.data.webLink + companyId);
        } catch (error) {
            throw error;
        }
    };

    const fetchPreview = async (client_secret) => {
        try {
            if (client_secret) {
                const response = await axiosInterceptor.get(apiRoute.webform.preview, { headers: { 'client-secret': client_secret } });
                setPreview(response.data);
            }
            dispatch({ type: SET_LOADING, loading: false });
        } catch (error) {
            throw error;
        }
    };

    useEffect(() => {
        fetchCredentials();
    }, []);

    useEffect(() => {
        dispatch({ type: setCurrentPage, currentPage: 'integrate-diolog' });
    }, []);

    useEffect(() => {
        if (changedState?.theme?.changed) {
            const primaryColor = changedState.theme.value?.primaryColor ?? defaultTheme.primaryColor;
            const secondaryColor = changedState.theme.value?.secondaryColor ?? defaultTheme.secondaryColor;
            const bgColor = changedState.theme.value?.backgroundColor ?? defaultTheme.bgColor;

            var iframe = document.getElementById('myIframe');
            var iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

            // Replace the theme of content inside iframe

            // Primary Color
            if (primaryColor) {
                const button = iframeDoc.getElementById('submit-button');
                const logo = iframeDoc.getElementById('logo');
                const policy = iframeDoc.getElementById('policy-link');
                var svgElement = logo;
                var pathElement = svgElement.querySelector('path');

                button.style.backgroundColor = primaryColor;
                policy.style.color = primaryColor;

                pathElement.setAttribute('fill', primaryColor);
            }

            //Secondary Color
            if (secondaryColor) {
                const header = iframeDoc.getElementById('header-body');
                header.style.backgroundColor = secondaryColor;
            }

            //BgColor
            if (bgColor) {
                var elementInsideIframe = iframeDoc.querySelector('.card-body');
                elementInsideIframe.style.backgroundColor = bgColor;

                var sectionInsideElement = elementInsideIframe.querySelector('section');
                sectionInsideElement.style.backgroundColor = bgColor;
            }
        }
    }, [changedState?.theme]);

    const copyValue = (value, message) => {
        dispatch({
            type: SET_TOAST_OBJECT,
            toastObject: {
                status: true,
                variant: 'success',
                message
            }
        });
        copy(value);
    };

    const handleClickCompanyId = () => {
        setShowCompanyId(!showCompanyId);
    };

    const handleClickSecret = () => {
        setShowSecret(!showSecret);
    };

    const onThemeChanged = (key, value) => {
        setChangedState({
            ...changedState,
            theme: {
                value: { ...changedState?.theme?.value, [key]: value },
                changed: true,
                showCancel: true // adding this extra parameter for cancel button on theme..since it has two condition. 1. remove cancel button and 2. reset theme when pressed
            }
        });
    };

    const resetTheme = () => {
        setChangedState({
            ...changedState,
            theme: {
                value: defaultTheme,
                changed: true
            }
        });
        setColor(defaultTheme);
        updateData('theme', defaultTheme);
    };

    const handleChange = (key, value) => {
        if (key === 'authorizedOrigins' && disableIframe) {
            setDisableIframe(false);
        }
        trackChange(key, value, true);
    };

    const trackChange = (key, value, changed = false) => {
        setChangedState({
            ...changedState,
            [key]: {
                value,
                changed
            }
        });
    };

    const onCancel = (key) => {
        const v = credentials[key] ?? changedState[key].value;

        //Set the value of theme to change true to reflect theme
        setChangedState({ ...changedState, [key]: { value: v, changed: key === 'theme', showCancel: false } });
        key === 'theme' && setColor(credentials.theme);
        setError({ [key]: null });
    };

    function isValidDomain(url) {
        const env = process.env?.REACT_APP_NODE_ENV;

        let regex = '';
        if (env === 'dev' || env === 'stage') {
            regex = /^(https?:\/\/)([a-zA-Z0-9]+\.)+[a-zA-Z]{2,6}$/;
        } else {
            regex = /^https:\/\/([a-zA-Z0-9]+\.)+[a-zA-Z]{2,6}$/;
        }

        return regex.test(url);
    }

    const updateData = async (key, payload) => {
        try {
            const field_to_update = changedState[key];

            if (!payload && !field_to_update.changed) {
                setError({ message: 'Please enter your website domain' });
                return;
            }

            if (key === 'authorizedOrigins') {
                const validateDomain = isValidDomain(field_to_update.value?.[0]);
                if (!validateDomain) {
                    setError({ message: 'Please enter a valid domain, such as "https://www.example.com".' });
                    return;
                }
            }
            const data = payload ?? changedState[key];
            dispatch({ type: SET_LOADING, loading: true });
            await axiosInterceptor.put(apiRoute.webform.update, { [key]: payload ?? field_to_update.value });
            dispatch({ type: SET_LOADING, loading: false });
            setChangedState({ ...changedState, [key]: { ...data, changed: false, showCancel: false } });
            setError({ [key]: null });
        } catch (error) {}
    };

    const getIframeLink = () => {
        return `<iframe src="${link}" title="diolog-web-form"></iframe>`;
    };

    const isEmail = (value) => {
        const emailRegex =
            /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;

        return emailRegex.test(value);
    };

    const CancelButton = ({ onCancel }) => (
        <Button
            variant="contained"
            onClick={onCancel}
            sx={{
                background: 'white',
                border: '1px solid #FBB414',
                color: '#00214D',
                '&:hover': {
                    background: '#FBB414',
                    color: '#00214d'
                }
            }}
        >
            Cancel
        </Button>
    );
    const hasEditPermission = hasNoPermissionToEdit(PermissionTypes.webform, pagePermissions);
    return (
        <Box pt={3} sx={styles}>
            <Stack rowGap={3}>
                <Typography variant="body1" color="#00214D" lineHeight="1.42857">
                    Integrate Diolog seamlessly into your current investor website or platform by following the instructions below.
                </Typography>
                <Typography variant="body1" color="#00214D" lineHeight="1.42857">
                    This will enable your investors to ask questions without the need for them to download the mobile app and allow you to
                    respond to questions submitted via a connected web form. Responses to investor questions submitted via the web form will
                    be received by email.
                </Typography>
                <Typography variant="body1" color="#676767" lineHeight="1.42857">
                    If you would like technical assistance, please contact help@diolog.com.au.
                </Typography>
            </Stack>
            <Divider sx={{ margin: '24px 0' }} />
            {!isAccountPremium ? (
                <PremiumPage
                    title="Upgrade to integrate Diolog questions"
                    subTitle=" Upgrade to engage effortlessly with all segments of your investor base by integrating Diolog into your company
                            platform."
                    image={Image}
                />
            ) : (
                <>
                    <Typography variant="h4" color="#00214D" fontWeight="500" mb={2}>
                        Diolog website API
                    </Typography>
                    <Stack
                        p={2}
                        gap={3}
                        sx={{
                            border: '1px solid rgba(125, 125, 130, 0.25)',
                            borderRadius: '12px'
                        }}
                    >
                        <Box>
                            <Typography variant="body1" color="#00214D" lineHeight="1.42857" mb={1} fontWeight={700}>
                                1. Enter the website domain where you would like to embed the Diolog form.
                                <CustomizedTooltip
                                    arrow
                                    placement="top"
                                    title="The domain sets which locations can use the web form. Requests from unregistered locations are denied access for safety."
                                    style={{ marginLeft: '10px', verticalAlign: 'middle', position: 'relative', color: '#7D7D82' }}
                                >
                                    <InfoIcon />
                                </CustomizedTooltip>
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                fontSize="0.75rem"
                                color="#7D7D82"
                                lineHeight="1.31"
                                letterSpacing="0.03em"
                                fontWeight="500"
                                mb={1}
                            >
                                Domain or URL
                            </Typography>
                            <Stack direction={{ md: 'row' }} gap={2}>
                                <Stack sx={{ width: { md: '50%' } }}>
                                    <TextField
                                        placeholder="https://example.com"
                                        variant="outlined"
                                        value={origin}
                                        key="origin"
                                        disabled={hasEditPermission}
                                        onChange={(e) => {
                                            const value = isEmail(e.target.value);
                                            if (value) {
                                                return;
                                            }
                                            handleChange('authorizedOrigins', [e.target.value]);
                                        }}
                                        autoComplete="off"
                                        name="origin"
                                    />
                                </Stack>
                                <PermisssionValidationWrapper pageName={'web-form'}>
                                    <Stack direction="row" sx={{ md: '50%' }} gap="12px">
                                        {changedState.authorizedOrigins?.changed && (
                                            <Button
                                                variant="contained"
                                                className="alternate"
                                                onClick={() => updateData('authorizedOrigins')}
                                            >
                                                Save
                                            </Button>
                                        )}
                                        {origin && changedState.authorizedOrigins?.changed && (
                                            <CancelButton onCancel={() => onCancel('authorizedOrigins')} />
                                        )}
                                    </Stack>
                                </PermisssionValidationWrapper>
                            </Stack>
                            {error?.message && (
                                <Typography color="error" mt={1}>
                                    {error?.message}
                                </Typography>
                            )}
                        </Box>
                        <Box>
                            <Typography variant="body1" color="#00214D" lineHeight="1.42857" mb={1} fontWeight={700}>
                                2. Copy and paste this code into the desired section of your website.
                                <CustomizedTooltip
                                    arrow
                                    placement="top"
                                    title="Embed directly in your HTML or as a message on the website that supports HTML. Adjust the location for best performance and visibility."
                                    style={{ marginLeft: '10px', verticalAlign: 'middle', position: 'relative', color: '#7D7D82' }}
                                >
                                    <InfoIcon />
                                </CustomizedTooltip>
                            </Typography>
                            <Stack direction={{ md: 'row' }} gap={2}>
                                <Stack sx={{ width: { md: '50%' } }}>
                                    <TextField
                                        value={disableIframe ? '' : getIframeLink()}
                                        variant="outlined"
                                        disabled={disableIframe}
                                        autoComplete="off"
                                    />
                                </Stack>
                                <Stack direction="row" sx={{ md: '50%' }} gap={2}>
                                    <Button
                                        variant="contained"
                                        className="alternate"
                                        onClick={() => copyValue(getIframeLink(), 'Code snippet copied!')}
                                        disabled={disableIframe}
                                    >
                                        Copy
                                    </Button>
                                </Stack>
                            </Stack>
                            <Typography mt={1} variant="caption" color="#7D7D82">
                                If you need assistance, forward this code to your website developer or contact Diolog.
                            </Typography>
                        </Box>
                    </Stack>
                    <Divider sx={{ margin: '24px 0' }} />
                    <Accordion>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="customize-preview-web-form"
                            id="customize-preview-web-form"
                        >
                            Customise and preview web form
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack
                                p={2}
                                sx={{
                                    border: '1px solid rgba(125, 125, 130, 0.25)',
                                    borderRadius: '12px'
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontSize="0.75rem"
                                    color="#7D7D82"
                                    lineHeight="1.31"
                                    letterSpacing="0.03em"
                                    fontWeight="500"
                                    mb={1}
                                >
                                    Choose colours
                                </Typography>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Stack direction="row" gap={3}>
                                        <Stack className="choose-color" direction="row" gap={3}>
                                            <Stack direction="row" gap={1} alignItems="center">
                                                {/* <MuiColorInput
                                            format="hex"
                                            value={changedState.theme?.value?.primaryColor ?? defaultTheme.primaryColor}
                                            onChange={(value) => onThemeChanged('primaryColor', value)}
                                            disableripple
                                        /> */}
                                                <ColorPicker
                                                    value={color.primaryColor ?? defaultTheme.primaryColor}
                                                    onChange={(value) => {
                                                        onThemeChanged('primaryColor', `#${value.hex}`);
                                                        setColor({ ...color, primaryColor: value });
                                                    }}
                                                    defaultValue={defaultTheme.primaryColor}
                                                    disableripple
                                                />
                                                <Typography fontSize="1rem" lineHeight="1.25" color="#00214D" fontWeight="500">
                                                    Primary
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" gap={1} alignItems="center">
                                                <ColorPicker
                                                    openAtStart={false}
                                                    value={color.secondaryColor ?? defaultTheme.secondaryColor}
                                                    onChange={(value) => {
                                                        onThemeChanged('secondaryColor', `#${value.hex}`);
                                                        setColor({ ...color, secondaryColor: value });
                                                    }}
                                                    defaultValue={defaultTheme.secondaryColor}
                                                    disableripple
                                                />
                                                <Typography fontSize="1rem" lineHeight="1.25" color="#00214D" fontWeight="500">
                                                    Secondary
                                                </Typography>
                                            </Stack>
                                            <Stack direction="row" gap={1} alignItems="center">
                                                <ColorPicker
                                                    value={color.backgroundColor ?? defaultTheme.backgroundColor}
                                                    onChange={(value) => {
                                                        onThemeChanged('backgroundColor', `#${value.hex}`);
                                                        setColor({ ...color, backgroundColor: value });
                                                    }}
                                                    defaultValue={defaultTheme.backgroundColor}
                                                    disableripple
                                                />
                                                <Typography fontSize="1rem" lineHeight="1.25" color="#00214D" fontWeight="500">
                                                    Background
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                        <PermisssionValidationWrapper pageName={PermissionTypes.webform}>
                                            <Stack direction="row" gap="12px">
                                                <Button variant="contained" className="alternate" onClick={() => updateData('theme')}>
                                                    Save
                                                </Button>
                                                {changedState.theme?.showCancel && <CancelButton onCancel={() => onCancel('theme')} />}
                                            </Stack>
                                        </PermisssionValidationWrapper>
                                    </Stack>
                                    <PermisssionValidationWrapper pageName={PermissionTypes.webform}>
                                        <Stack>
                                            <Button
                                                variant="text"
                                                sx={{
                                                    color: '#00214D',
                                                    '&:hover': {
                                                        background: '#fcb413 '
                                                    }
                                                }}
                                                onClick={resetTheme}
                                            >
                                                Reset
                                            </Button>
                                        </Stack>
                                    </PermisssionValidationWrapper>
                                </Stack>
                            </Stack>
                            <Divider sx={{ margin: '24px 0' }} />
                            <iframe
                                id="myIframe"
                                srcDoc={preview}
                                title="preview"
                                style={{
                                    border: 'none',
                                    height: 1165,
                                    width: '100%',
                                    borderRadius: 16,
                                    stroke: '#E5E5E8',
                                    border: '1px solid #E5E5E8',
                                    backgroundColor: changedState.theme?.value?.backgroundColor ?? defaultTheme.backgroundColor
                                }}
                            ></iframe>
                        </AccordionDetails>
                    </Accordion>
                    <Divider sx={{ margin: '24px 0' }} />
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="advanced-details" id="advanced-details">
                            Advanced details
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack gap={3}>
                                <Stack gap="12px">
                                    <Typography
                                        variant="subtitle1"
                                        fontSize="0.75rem"
                                        color="#7D7D82"
                                        lineHeight="1.31"
                                        letterSpacing="0.03em"
                                        fontWeight="500"
                                    >
                                        Secret ID
                                        <CustomizedTooltip
                                            arrow
                                            placement="top"
                                            title="Secret ID"
                                            style={{ marginLeft: '10px', verticalAlign: 'middle', position: 'relative' }}
                                        >
                                            <InfoIcon />
                                        </CustomizedTooltip>
                                    </Typography>
                                    <Stack direction={{ md: 'row' }} gap={2}>
                                        <Stack sx={{ width: { md: '50%' } }}>
                                            <TextField
                                                size="small"
                                                type={showSecret ? 'text' : 'password'}
                                                // label="Password"
                                                value={credentials.clientSecret}
                                                // onChange={handlePassword}
                                                required={true}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                aria-label="toggle password visibility"
                                                                onClick={handleClickSecret}
                                                                edge="end"
                                                                disableripple
                                                                disabled={hasEditPermission}
                                                            >
                                                                {showSecret ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                                fullWidth
                                            />
                                        </Stack>
                                        <Button
                                            variant="contained"
                                            className="alternate"
                                            onClick={() => copyValue(credentials.clientSecret, 'Secret copied!')}
                                            disabled={hasEditPermission}
                                        >
                                            Copy
                                        </Button>
                                    </Stack>
                                </Stack>
                                <Stack gap="12px">
                                    <Typography
                                        variant="subtitle1"
                                        fontSize="0.75rem"
                                        color="#7D7D82"
                                        lineHeight="1.31"
                                        letterSpacing="0.03em"
                                        fontWeight="500"
                                    >
                                        Company ID
                                        <CustomizedTooltip
                                            arrow
                                            placement="top"
                                            title="Company ID"
                                            style={{ marginLeft: '10px', verticalAlign: 'middle', position: 'relative' }}
                                        >
                                            <InfoIcon />
                                        </CustomizedTooltip>
                                    </Typography>
                                    <Stack direction={{ md: 'row' }} gap={2}>
                                        <Stack sx={{ width: { md: '50%' } }}>
                                            <TextField
                                                size="small"
                                                type={showCompanyId ? 'text' : 'password'}
                                                // label="Password"
                                                value={credentials.companyId}
                                                // onChange={handlePassword}
                                                required={true}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                aria-label="toggle password visibility"
                                                                onClick={handleClickCompanyId}
                                                                edge="end"
                                                                disableripple
                                                                disabled={hasEditPermission}
                                                            >
                                                                {showCompanyId ? <VisibilityOff /> : <Visibility />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                                fullWidth
                                            />
                                        </Stack>
                                        <Button
                                            variant="contained"
                                            className="alternate"
                                            disabled={hasEditPermission}
                                            onClick={() => copyValue(credentials.companyId, 'CompanyId copied!')}
                                        >
                                            Copy
                                        </Button>
                                    </Stack>
                                </Stack>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>
                </>
            )}
        </Box>
    );
};

export default WebForm;
